import Card from 'components/Card/Card'
import SectionContent from 'components/Section/SectionContent'
import { getAccessToken, getConfig, getPartnerId, getUA } from 'core/singleton'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { NavLink, useLocation } from 'react-router-dom'
import SkeletonCard from 'components/Skeleton/SkeletonCard'
import SectionTitle from 'components/Section/SectionTitle'
import { IconLoadMore, IconRear } from 'components/Icons'
import { useRecoilValue } from 'recoil'
import { encodePercent } from 'core/util/search'
import { BRIDGE, ONLY_POST_MSG_PARTNERS } from 'core/constants/bridge'
import { IS_OPEN_LINK, STORE_MODULE, STORE_ROOT_PROPS } from 'core/store/global'
import { getExecutorForPartner } from 'core/util/partner'
import { getUrlQueryParameter, moveToLink, parseObjectToQueryString } from 'core/util/url'
import { filterObject } from 'core/util/object'
import { STORE_CURATION_LOADING } from 'core/store/curation'

import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { CUSTOM_CLASS_NAME, LINK_MOVE_TYPE } from 'core/constants'
import { STORE_AIRBRIDGE, STORE_MARKETING_GTM } from 'core/store/analysis'
import { useAsync } from 'react-async'
import API from 'core/api'
import { getLocation, layoutSmall, requestBridge } from 'core/util/default'
import SectionLayout from 'container/SectionLayout'
import { BroadcastParams } from '@types'

interface SectionCurationDetailProps {
  curationItem: StoreCurationItems
}

const SectionCurationDetail: React.FC<SectionCurationDetailProps> = (props) => {
  const { curationItem } = props
  const [nextKey, setNextKey] = useState<string>('init')
  const [broadcastItem, setBroadcastItem] = useState<CurationDetailItem[]>([])
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { allWording } = getConfig()
  const location = useLocation()
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)
  const isCurationLoading = useRecoilValue(STORE_CURATION_LOADING)
  const moduleName = useRecoilValue(STORE_MODULE)

  const partnerId = getPartnerId()
  const isBridge = root?.bridge
  const curationId = root?.curationId
  const { isSDK } = getUA()

  const query = getUrlQueryParameter()
  const isCafe24Store = useMemo(() => {
    return partnerId === 'demo06'
  }, [partnerId])

  const { data, isLoading, reload } = useAsync({
    promiseFn: API.getCurationDetail,
    curationId,
    nextKey,
    limit: 10,
    moduleName,
  })

  const handleClickBroadcast = useCallback(
    ({
        link,
        broadcastId,
        broadcastTypeCode,
        broadcastName,
      }: {
        link?: string
        broadcastId?: string
        isBridge?: boolean
        broadcastTypeCode?: string
        broadcastName?: string
        order?: number
      }) =>
      () => {
        if (!link || isCafe24Store) return

        if (marketingGTM?.checkAvailability()) {
          const queryStringPrefix = link.indexOf('?') >= 0 ? '&' : '?'
          link = link.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
        }

        const params: BroadcastParams = {
          broadcastUrl: link,
        }
        if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
          params.broadcastId = broadcastId
        }

        new GAHelper().sendEvent(GA_EVENT.CLICK_CONTENT, {
          partnersId: partnerId,
          location: getLocation(location),
          broadcastTypeCode: broadcastTypeCode,
          broadcastName: broadcastName,
          position: curationItem?.curationName,
        })

        if (getExecutorForPartner(partnerId)?.reqMoveBroadcastBridge?.(link)) {
          return
        }
        if (root?.bridge || window[BRIDGE.BROADCAST] || isSDK) {
          requestBridge(BRIDGE.BROADCAST, params)
        } else {
          moveToLink({
            airbridge,
            link,
            type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
          })
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      airbridge,
      partnerId,
      location,
      curationItem.curationName,
      isOpenLink,
      isCafe24Store,
      marketingGTM,
      isSDK,
    ],
  )

  /**
   * @name DOMElement
   **/
  const curationBroadcastList = useMemo(() => {
    if (curationItem) {
      const token = getAccessToken()

      return curationItem.curationDetailList.map((item, i) => {
        const {
          broadcastId,
          broadcastName: title,
          broadcastStateCode,
          broadcastThumbnailList,
          outputPath,
          partnerProfileImageUrl: shopLogo,
          previewUrl,
          room,
          shopName,
          totalDuration: playTime,
          usePurchaseEvent: isShowPurchaseAuthBadge,
        } = item
        const incomingCounterInfo = room?.incomingCounterInfo

        const representImage = broadcastThumbnailList?.filter(
          ({ isRepresentative }) => isRepresentative,
        )

        const queryString = parseObjectToQueryString({
          accessToken: token,
          npc: curationItem.curationId,
          ...filterObject(query, (k) => Boolean(k.startsWith('utm'))),
        })
        const link = outputPath + queryString
        const isLive = broadcastStateCode === 'onair'

        return (
          <Card
            small
            key={`${broadcastId}_${i}`}
            className={CUSTOM_CLASS_NAME.CURATION_CARD}
            broadcastId={broadcastId}
            broadcastStateCode={broadcastStateCode}
            link={link}
            isOpenLink={isOpenLink}
            stream={previewUrl ?? undefined}
            header={{ isLive, playTime, incomingCounterInfo }}
            bottom={{ isShowPurchaseAuthBadge }}
            thumbnail={`${representImage?.[0]?.thumbnailUrl}?h=732&w=548&f=jpg`}
            info={{ shopName, title, shopLogo }}
            isBridge={!!isBridge}
            root={root}
            order={i}
            handleClickBroadcast={handleClickBroadcast}
          />
        )
      })
    }
  }, [curationItem, query, isOpenLink, isBridge, root, handleClickBroadcast])

  const skeleton = useMemo(() => {
    if (isCurationLoading || isLoading) {
      return (
        <>
          <SkeletonCard small className={CUSTOM_CLASS_NAME.CURATION_CARD_SKELETON} />
          <SkeletonCard small className={CUSTOM_CLASS_NAME.CURATION_CARD_SKELETON} />
          <SkeletonCard small className={CUSTOM_CLASS_NAME.CURATION_CARD_SKELETON} />
          <SkeletonCard small className={CUSTOM_CLASS_NAME.CURATION_CARD_SKELETON} />
          <SkeletonCard small className={CUSTOM_CLASS_NAME.CURATION_CARD_SKELETON} />
          <SkeletonCard small className={CUSTOM_CLASS_NAME.CURATION_CARD_SKELETON} />
        </>
      )
    }
  }, [isCurationLoading, isLoading])

  const getHistoryUrl = (title: string) => () => {
    const encodePercentTitle = encodePercent(title)
    return `/curation/${curationItem.curationId}/${encodeURIComponent(encodePercentTitle)}/${
      location.search
    }`
  }
  /**
   * @name curationIdList
   * 큐레이션 모듈화 사용시의 방송 리스트
   */
  const curationIdBroadcastList = useMemo(() => {
    if (broadcastItem) {
      const token = getAccessToken()

      return broadcastItem?.map((item, i) => {
        const {
          broadcastId,
          broadcastName: title,
          broadcastStateCode,
          broadcastThumbnailList,
          outputPath,
          partnerProfileImageUrl: shopLogo,
          previewUrl,
          room,
          shopName,
          totalDuration: playTime,
          usePurchaseEvent: isShowPurchaseAuthBadge,
        } = item
        const incomingCounterInfo = room?.incomingCounterInfo

        const representImage = broadcastThumbnailList?.filter(
          ({ isRepresentative }) => isRepresentative,
        )

        const queryString = parseObjectToQueryString({
          accessToken: token,
          npc: curationItem.curationId,
          ...filterObject(query, (k) => Boolean(k.startsWith('utm'))),
        })
        const link = outputPath + queryString
        const isLive = broadcastStateCode === 'onair'

        return (
          <Card
            small
            key={`${broadcastId}_${i}`}
            className={CUSTOM_CLASS_NAME.CURATION_CARD}
            broadcastId={broadcastId}
            broadcastStateCode={broadcastStateCode}
            link={link}
            isOpenLink={isOpenLink}
            stream={previewUrl ?? undefined}
            header={{ isLive, playTime, incomingCounterInfo }}
            bottom={{ isShowPurchaseAuthBadge }}
            thumbnail={`${representImage?.[0]?.thumbnailUrl}?h=732&w=548&f=jpg`}
            info={{ shopName, title, shopLogo }}
            isBridge={!!isBridge}
            root={root}
            order={i}
            handleClickBroadcast={handleClickBroadcast}
          />
        )
      })
    }
  }, [
    broadcastItem,
    curationItem.curationId,
    query,
    isOpenLink,
    isBridge,
    root,
    handleClickBroadcast,
  ])

  const handleInterSection = useCallback(() => {
    let isIntersecting = false
    return new IntersectionObserver(
      (entires) => {
        if (entires[0].isIntersecting && !isIntersecting && nextKey) {
          reload()
          isIntersecting = true
        }
      },
      {
        root: null,
        threshold: 1,
        rootMargin: '0px 0px 0px 0px',
      },
    )
  }, [nextKey, reload])

  useEffect(() => {
    if (data?.items) {
      setBroadcastItem((prev) => [...prev, ...data.items])
      const nextKey = data?.nextKey
      setNextKey(nextKey)
    }
  }, [data])

  useEffect(() => {
    if (!loadMoreRef.current) return

    handleInterSection().observe(loadMoreRef.current)
    loadMoreRef.current.focus()

    return () => handleInterSection().disconnect()
  }, [handleInterSection])

  return (
    <SectionLayout
      elementId={`curationId-${curationItem.curationId}`}
      className={CUSTOM_CLASS_NAME.CURATION}
      trigger={curationId}
    >
      <StyledSectionCurationDetail id={curationItem.curationId}>
        <SectionTitle className={CUSTOM_CLASS_NAME.CURATION_TITLE}>
          <span>{curationItem.curationName}</span>
          {!isCafe24Store && !root?.curationId && (
            <NavLink
              to={getHistoryUrl(curationItem.curationName)}
              className="show_all"
              onClick={() => {
                new GAHelper().sendEvent(GA_EVENT.CLICK_MORE, {
                  partnersId: partnerId,
                  location: getLocation(location),
                  position: curationItem.curationName,
                })
              }}
            >
              {allWording === 'IconRear' ? <IconRear /> : allWording}
            </NavLink>
          )}
        </SectionTitle>
        <SectionContent className={`curation_section ${CUSTOM_CLASS_NAME.CURATION_CONTENTS}`}>
          {/* 스켈레톤 */}
          {skeleton}
          {/* 큐레이션 상세 */}
          {curationId ? curationIdBroadcastList : curationBroadcastList}
          {nextKey && curationId && (
            <StyledLoadMoreCard>
              <div className="card_load_more_wrapper" ref={loadMoreRef}>
                <button>
                  <IconLoadMore />
                </button>
              </div>
            </StyledLoadMoreCard>
          )}
        </SectionContent>
      </StyledSectionCurationDetail>
    </SectionLayout>
  )
}

const StyledSectionCurationDetail = styled.div`
  width: 100%;
  position: relative;
  .show_all {
    color: ${(props) => props.theme.all};
    background: none;
    cursor: pointer;
    border: none;
    margin-left: auto;
    font-size: 14px;
    font-weight: 700;
    display: none;
    ${(props) =>
      props.theme.curationAll &&
      css`
        display: block;
      `}
  }
`
const StyledLoadMoreCard = styled.div`
  .card_load_more_wrapper {
    width: 100px;
    display: flex;
    button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: inherit;
      height: 100%;
      background-color: transparent;
      border: none;
      color: ${(props) => props.theme.loading};
      cursor: pointer;
      > div {
        font-size: 14px;
        margin-top: 12px;
        color: ${(props) => props.theme.font.primary};
      }
      path:first-of-type {
        fill: ${(props) => props.theme.font.primary};
      }
      path:last-of-type {
        fill: ${(props) => props.theme.background};
      }
    }
  }

  ${layoutSmall(css`
    .card_load_more_wrapper {
      width: 100px;
      height: 282px;
    }
  `)}
`

export default SectionCurationDetail
